// Copyright (c) Ping. All rights reserved.
//
// Probes a picked video File entirely in the browser: loads it into a
// detached <video> to read intrinsic width/height + duration, then seeks
// a hair past the start and paints that frame to a canvas to mint a
// poster thumbnail. Used by the composer tray (staged preview) and the
// send path (poster blob shipped alongside the video). No bytes leave
// the device here — everything is local object-URL work.

export interface ProbedVideo {
  readonly width: number;
  readonly height: number;
  readonly durationMs: number;
  /** Object URL of the source file (for the staged-tray preview + echo). */
  readonly objectUrl: string;
  /** Poster JPEG bytes (first frame) — undefined if the frame couldn't be drawn. */
  readonly posterBytes?: Uint8Array | undefined;
  readonly posterMime: string;
  /** Object URL of the poster blob (for the staged-tray thumbnail). */
  readonly posterObjectUrl?: string | undefined;
}

const POSTER_MIME = 'image/jpeg';

export async function probeVideoFile(file: File): Promise<ProbedVideo> {
  const objectUrl = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.muted = true;
  video.playsInline = true;
  video.src = objectUrl;

  const meta = await new Promise<{ w: number; h: number; durMs: number }>(
    (resolve, reject) => {
      const onMeta = (): void => {
        resolve({
          w: video.videoWidth,
          h: video.videoHeight,
          durMs: Number.isFinite(video.duration) ? Math.round(video.duration * 1000) : 0,
        });
      };
      video.addEventListener('loadedmetadata', onMeta, { once: true });
      video.addEventListener(
        'error',
        () => reject(new Error('video-metadata-failed')),
        { once: true },
      );
    },
  );

  // Best-effort poster: seek slightly past 0 (frame 0 is often black) and
  // capture. Failures degrade to "no poster" — the tile still renders.
  let posterBytes: Uint8Array | undefined;
  let posterObjectUrl: string | undefined;
  try {
    const blob = await capturePoster(video, meta.w, meta.h, meta.durMs);
    if (blob !== null) {
      posterBytes = new Uint8Array(await blob.arrayBuffer());
      posterObjectUrl = URL.createObjectURL(blob);
    }
  } catch {
    // No poster — acceptable.
  }

  return {
    width: meta.w,
    height: meta.h,
    durationMs: meta.durMs,
    objectUrl,
    posterMime: POSTER_MIME,
    ...(posterBytes !== undefined ? { posterBytes } : {}),
    ...(posterObjectUrl !== undefined ? { posterObjectUrl } : {}),
  };
}

function capturePoster(
  video: HTMLVideoElement,
  w: number,
  h: number,
  durMs: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    if (w === 0 || h === 0) {
      resolve(null);
      return;
    }
    const onSeeked = (): void => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (ctx === null) {
          resolve(null);
          return;
        }
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob((b) => resolve(b), POSTER_MIME, 0.82);
      } catch {
        resolve(null);
      }
    };
    video.addEventListener('seeked', onSeeked, { once: true });
    video.addEventListener('error', () => resolve(null), { once: true });
    // Seek ~100ms in (clamped) to skip a leading black frame.
    try {
      video.currentTime = Math.min(0.1, durMs > 0 ? durMs / 1000 / 2 : 0.1);
    } catch {
      resolve(null);
    }
  });
}

/** "0:13" duration label from milliseconds. */
export function formatVideoDuration(durationMs: number): string {
  const totalSec = Math.max(0, Math.floor(durationMs / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}
